import axios from 'axios';

const state = {
  trips: [],
  currentTrip: null,
  loading: false,
  error: null
};

const mutations = {
  SET_TRIPS(state, trips) {
    state.trips = trips;
  },
  SET_CURRENT_TRIP(state, trip) {
    state.currentTrip = trip;
  },
  ADD_TRIP(state, trip) {
    state.trips.unshift(trip);
  },
  UPDATE_TRIP(state, updatedTrip) {
    const index = state.trips.findIndex(trip => trip.id === updatedTrip.id);
    if (index !== -1) {
      state.trips.splice(index, 1, updatedTrip);
    }
  },
  DELETE_TRIP(state, tripId) {
    state.trips = state.trips.filter(trip => trip.id !== tripId);
  },
  SET_LOADING(state, status) {
    state.loading = status;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  }
};

const actions = {
    async fetchTrips({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await axios.get('/admin/trips');
      commit('SET_TRIPS', response.data.trips);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch trips';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

    async fetchTripById({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await axios.get(`/admin/trips/${id}`);
      commit('SET_CURRENT_TRIP', response.data.trip);
      return response.data.trip;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to fetch trip';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createTrip({ commit }, tripData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const formData = new FormData();

      // Add all fields (including URLs)
      Object.keys(tripData).forEach(key => {
        if (tripData[key] !== null && tripData[key] !== undefined) {
          if (key === 'images' && Array.isArray(tripData[key])) {
            // Send images as comma-separated URLs
            formData.append(key, tripData[key].join(','));
          } else {
            formData.append(key, tripData[key]);
          }
        } else if (key === 'video' || key === 'itineraryPdf') {
          // Explicitly send empty string to remove these fields
          formData.append(key, '');
        }
      });

      const response = await axios.post('/admin/trips', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      commit('ADD_TRIP', response.data.trip);
      return { success: true, trip: response.data.trip, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to create trip';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateTrip({ commit }, { id, tripData }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const formData = new FormData();

      // Add all fields (including URLs)
      Object.keys(tripData).forEach(key => {
        if (tripData[key] !== null && tripData[key] !== undefined) {
          if (key === 'images' && Array.isArray(tripData[key])) {
            // Send images as comma-separated URLs
            formData.append(key, tripData[key].join(','));
          } else {
            formData.append(key, tripData[key]);
          }
        } else if (key === 'video' || key === 'itineraryPdf') {
          // Explicitly send empty string to remove these fields
          formData.append(key, '');
        }
      });

      const response = await axios.put(`/admin/trips/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      commit('UPDATE_TRIP', response.data.trip);
      return { success: true, trip: response.data.trip, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update trip';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteTrip({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await axios.delete(`/admin/trips/${id}`);
      commit('DELETE_TRIP', id);
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete trip';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  clearCurrentTrip({ commit }) {
    commit('SET_CURRENT_TRIP', null);
  }
};

const getters = {
  allTrips: state => state.trips,
  currentTrip: state => state.currentTrip,
  isLoading: state => state.loading,
  error: state => state.error,
  tripsCount: state => state.trips.length
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
